/**
 * Réalisations : les chantiers publiés.
 *
 * Elles ne relèvent pas du garde-fou éditorial — aucun seuil de mots ne les
 * concerne — mais elles y participent : une réalisation publiée dans une
 * commune dispense la page de cette commune de son seuil de contenu local.
 * L'écran le rappelle plutôt que de laisser deviner le lien.
 *
 * Les photos ne se choisissent pas ici. Le rattachement des médias appartient à
 * la médiathèque ; l'écran affiche ce qui est déjà lié et n'y touche pas, plutôt
 * que d'offrir un champ qui effacerait la sélection au premier enregistrement.
 */
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import {
  ContenuAtelier,
  PiedFormulaire,
  Section,
  type Selection,
  champsModifies,
  erreurChamp,
  slugifier,
  useFiche,
} from "../composants/ContenuAtelier";
import {
  type Realisation,
  useCommunes,
  useEnregistrer,
  usePrestations,
  useRealisations,
  useSupprimer,
} from "../composants/ContenuDonnees";
import { ChampsSeo } from "../composants/ContenuSeo";
import { EditeurRiche } from "../composants/EditeurRiche";

type Brouillon = {
  slug: string;
  titre: string;
  resume: string;
  contenu: string;
  /** `""` vaut « aucune commune », transmis à l'API comme `null`. */
  communeId: number | "";
  prestations: number[];
  budgetIndicatif: string;
  /** Chaîne pendant la saisie ; vide vaut « non renseigné », donc `null`. */
  dureeJours: string;
  metaTitle: string;
  metaDescription: string;
  isPublished: boolean;
};

const VIDE: Brouillon = {
  slug: "",
  titre: "",
  resume: "",
  contenu: "",
  communeId: "",
  prestations: [],
  budgetIndicatif: "",
  dureeJours: "",
  metaTitle: "",
  metaDescription: "",
  isPublished: false,
};

function depuis(realisation: Realisation): Brouillon {
  return {
    slug: realisation.slug,
    titre: realisation.titre,
    resume: realisation.resume,
    contenu: realisation.contenu,
    communeId: realisation.communeId ?? "",
    prestations: realisation.prestations,
    budgetIndicatif: realisation.budgetIndicatif,
    dureeJours: realisation.dureeJours === null ? "" : String(realisation.dureeJours),
    metaTitle: realisation.metaTitle,
    metaDescription: realisation.metaDescription,
    isPublished: realisation.isPublished,
  };
}

function pourApi(champs: Record<string, unknown>): Record<string, unknown> {
  const converti: Record<string, unknown> = { ...champs };

  if ("communeId" in converti) {
    converti.communeId = converti.communeId === "" ? null : Number(converti.communeId);
  }
  if ("dureeJours" in converti) {
    const brut = String(converti.dureeJours).trim();
    converti.dureeJours = brut === "" ? null : Number(brut);
  }

  return converti;
}

const cheminPublic = (slug: string) => `/realisations/${slug}/`;

export function ContenuRealisations() {
  const realisations = useRealisations();
  const communes = useCommunes();
  const prestations = usePrestations();
  const enregistrement = useEnregistrer<Realisation>("realisations");
  const suppression = useSupprimer("realisations");
  const fiche = useFiche<Brouillon>(VIDE);

  const liste = realisations.data ?? [];
  const courante =
    typeof fiche.selection === "number"
      ? liste.find((realisation) => realisation.id === fiche.selection)
      : undefined;

  const nomCommune = (id: number | null) =>
    communes.data?.find((commune) => commune.id === id)?.nom;

  function selectionner(cible: Selection) {
    enregistrement.reset();
    suppression.reset();

    if (cible === null) return fiche.fermer();
    if (cible === "nouveau") return fiche.ouvrir("nouveau", VIDE);

    const realisation = liste.find((element) => element.id === cible);
    if (realisation) fiche.ouvrir(cible, depuis(realisation));
  }

  function enregistrer() {
    const id = typeof fiche.selection === "number" ? fiche.selection : null;
    const champs =
      id === null ? { ...fiche.brouillon } : champsModifies(fiche.brouillon, fiche.reference);
    if (Object.keys(champs).length === 0) return;

    enregistrement.mutate(
      { id, champs: pourApi(champs) },
      {
        onSuccess: (realisation) => {
          if (realisation) fiche.confirmer(realisation.id, depuis(realisation));
        },
      },
    );
  }

  function supprimer() {
    if (typeof fiche.selection !== "number") return;
    suppression.mutate(fiche.selection, { onSuccess: () => fiche.fermer() });
  }

  const erreur = enregistrement.error ?? suppression.error ?? null;
  const brouillon = fiche.brouillon;

  return (
    <ContenuAtelier
      intro="Les chantiers publiés. Une réalisation rattachée à une commune fait vivre la page de cette commune — et la dispense du seuil de contenu local."
      entrees={liste.map((realisation) => ({
        id: realisation.id,
        libelle: realisation.titre,
        secondaire: nomCommune(realisation.communeId) ?? "Commune non renseignée",
        publie: realisation.isPublished,
      }))}
      selection={fiche.selection}
      surSelection={selectionner}
      libelleNouveau="Nouvelle réalisation"
      modifie={fiche.modifie}
      chargement={realisations.isPending}
      erreur={realisations.error ?? null}
      invite="Choisissez une réalisation à modifier, ou créez-en une."
    >
      <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
        <Stack spacing={4}>
          <Section titre="Identité">
            <TextField
              fullWidth
              label="Titre"
              value={brouillon.titre}
              onChange={(evenement) => {
                const titre = evenement.target.value;
                fiche.modifier(
                  fiche.selection === "nouveau" ? { titre, slug: slugifier(titre) } : { titre },
                );
              }}
              error={erreurChamp(erreur, "titre") !== undefined}
              helperText={erreurChamp(erreur, "titre")}
            />

            <TextField
              fullWidth
              label="Slug (URL)"
              value={brouillon.slug}
              onChange={(evenement) => fiche.modifier({ slug: evenement.target.value })}
              error={erreurChamp(erreur, "slug") !== undefined}
              helperText={erreurChamp(erreur, "slug") ?? `delsa.pro${cheminPublic(brouillon.slug)}`}
            />

            <FormControl fullWidth>
              <InputLabel id="etiquette-commune">Commune</InputLabel>
              <Select<number | "">
                labelId="etiquette-commune"
                label="Commune"
                value={brouillon.communeId}
                onChange={(evenement) =>
                  fiche.modifier({
                    communeId: evenement.target.value === "" ? "" : Number(evenement.target.value),
                  })
                }
              >
                <MenuItem value="">Aucune</MenuItem>
                {(communes.data ?? []).map((commune) => (
                  <MenuItem key={commune.id} value={commune.id}>
                    {commune.nom} ({commune.codePostal})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel id="etiquette-prestations">Prestations concernées</InputLabel>
              <Select
                multiple
                labelId="etiquette-prestations"
                input={<OutlinedInput label="Prestations concernées" />}
                value={brouillon.prestations}
                onChange={(evenement) => {
                  const valeur = evenement.target.value;
                  fiche.modifier({
                    prestations:
                      typeof valeur === "string" ? valeur.split(",").map(Number) : valeur,
                  });
                }}
                renderValue={(selectionnees) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selectionnees.map((id) => (
                      <Chip
                        key={id}
                        size="small"
                        label={
                          prestations.data?.find((prestation) => prestation.id === id)?.nom ??
                          `#${id}`
                        }
                      />
                    ))}
                  </Box>
                )}
              >
                {(prestations.data ?? []).map((prestation) => (
                  <MenuItem key={prestation.id} value={prestation.id}>
                    {prestation.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                fullWidth
                label="Budget indicatif"
                value={brouillon.budgetIndicatif}
                onChange={(evenement) =>
                  fiche.modifier({ budgetIndicatif: evenement.target.value })
                }
                error={erreurChamp(erreur, "budgetIndicatif") !== undefined}
                helperText={
                  erreurChamp(erreur, "budgetIndicatif") ??
                  "Fourchette telle qu'elle sera affichée, sans promesse de prix ferme."
                }
              />
              <TextField
                fullWidth
                label="Durée (jours)"
                value={brouillon.dureeJours}
                onChange={(evenement) => fiche.modifier({ dureeJours: evenement.target.value })}
                error={erreurChamp(erreur, "dureeJours") !== undefined}
                helperText={erreurChamp(erreur, "dureeJours") ?? "Laisser vide si non renseigné."}
              />
            </Stack>

            <FormControlLabel
              control={
                <Switch
                  checked={brouillon.isPublished}
                  onChange={(evenement) =>
                    fiche.modifier({ isPublished: evenement.target.checked })
                  }
                />
              }
              label="Publier cette réalisation"
            />

            {courante && (
              <Alert severity="info">
                {courante.medias.length === 0
                  ? "Aucune photo n'est rattachée. Le rattachement se fait depuis la médiathèque."
                  : `${courante.medias.length} photo${courante.medias.length > 1 ? "s" : ""} rattachée${courante.medias.length > 1 ? "s" : ""}. Elles se gèrent depuis la médiathèque ; cet écran n'y touche pas.`}
              </Alert>
            )}
          </Section>

          <Section titre="Rédaction">
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Résumé"
              value={brouillon.resume}
              onChange={(evenement) => fiche.modifier({ resume: evenement.target.value })}
              error={erreurChamp(erreur, "resume") !== undefined}
              helperText={
                erreurChamp(erreur, "resume") ?? "Phrase affichée dans la galerie des réalisations."
              }
            />

            <EditeurRiche
              libelle="Récit du chantier"
              valeur={brouillon.contenu}
              surChangement={(html) => fiche.modifier({ contenu: html })}
              hauteurMin={300}
              aide="Ce qui a été fait, dans quelles contraintes, avec quel résultat."
            />
          </Section>

          <Section titre="Référencement">
            <ChampsSeo
              metaTitle={brouillon.metaTitle}
              metaDescription={brouillon.metaDescription}
              surTitre={(metaTitle) => fiche.modifier({ metaTitle })}
              surDescription={(metaDescription) => fiche.modifier({ metaDescription })}
              chemin={cheminPublic(brouillon.slug || "…")}
              erreurTitre={erreurChamp(erreur, "metaTitle")}
              erreurDescription={erreurChamp(erreur, "metaDescription")}
            />
          </Section>
        </Stack>

        <PiedFormulaire
          modifie={fiche.modifie}
          enregistrement={enregistrement.isPending}
          surEnregistrer={enregistrer}
          surSupprimer={typeof fiche.selection === "number" ? supprimer : undefined}
          suppression={suppression.isPending}
          quoi="cette réalisation"
          erreur={erreur}
          succes={fiche.succes}
        />
      </Paper>
    </ContenuAtelier>
  );
}
